/**
 * Owner: Masa Bayu
 * File: use-hub-session.ts
 * Date:Time: 2026-10-22 10:22
 * Description: §457 F4 — bootstrap identiti qxread daripada qxp-hub sahaja.
 *   F7: nama pemanggil dipendekkan daripada nama perisai era projek lama
 *   kepada `useHubSession` / `HubSessionBinding`; kelakuan tidak berubah.
 *
 *   Punca kebenaran: stor sesi hub (`hub-auth.ts`) ialah SSOT — ia berdiri
 *   walaupun token ditulis oleh laluan lain (daftar beli di hub-sync), jadi
 *   hook ini melanggannya apabila status berubah. Inilah jambatan yang dulu
 *   tiada: daftar melalui panel beli → header & /account kini kenali pengguna.
 */

import { useCallback, useEffect, useState } from 'react';
import { useHubAuthState } from '../publication/hub-auth';
import { fetchSession, logoutSession } from './hub-session';
import type { AuthSessionView } from './session-types';

export interface HubSessionBinding {
  readonly sessionReady: boolean;
  readonly signedIn: boolean;
  readonly signedInName: string | null;
  readonly authSession: AuthSessionView | null;
  readonly onSignedIn: (session: AuthSessionView) => void;
  readonly onSignOut: () => void;
}

export function useHubSession(): HubSessionBinding {
  const [authSession, setAuthSession] = useState<AuthSessionView | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const hubAuth = useHubAuthState();

  useEffect(() => {
    if (hubAuth !== 'signed-in') {
      // 'anonymous' = tiada token; 'expired' = token ditolak server. Kedua-duanya
      // mesti buang identiti, jika tidak header terus paparan nama basi.
      setAuthSession(null);
      setSessionReady(true);
      return;
    }
    let cancelled = false;
    void fetchSession()
      .then((session) => {
        if (!cancelled && session !== null) setAuthSession(session);
      })
      .catch(() => {
        /* token ada tetapi /me gagal — kekalkan sesi setempat, jangan tolak
           pengguna aktif ke luar hanya kerana rangkaian terputus */
      })
      .finally(() => {
        if (!cancelled) setSessionReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [hubAuth]);

  const onSignedIn = useCallback((session: AuthSessionView) => {
    setAuthSession(session);
    setSessionReady(true);
  }, []);

  const onSignOut = useCallback(() => {
    // §415: log keluar mesti membuang token, bukan hanya keadaan React —
    // jika tidak pengguna nampak keluar tetapi sesi masih sah.
    void logoutSession();
    setAuthSession(null);
  }, []);

  const account = authSession?.account ?? null;
  return {
    sessionReady,
    signedIn: account !== null,
    signedInName: account !== null ? account.profile.displayName : null,
    authSession,
    onSignedIn,
    onSignOut,
  };
}
